import classNames from 'classnames';
import $ from 'jquery';
import JSON5 from 'json5';
const {parse} = JSON5;
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert} from 'Asserts';
import {requestAccess} from 'auth/RequestAccess';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  linkComponentDidMount,
  linkComponentDidUpdate,
  linkComponentWillUnmount,
  openLink,
} from 'link-preview/LinkUtil';
import {openScratchPlateChipModal} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewer';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
// @ts-expect-error
import formatters from 'notebook/mediocre/formatted-text/formatters';
import FormattersConstants, {
  Format,
  FormatData,
  FormatDataTrigger,
} from 'notebook/mediocre/formatted-text/FormattersConstants';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import useEntryChipState, {UseEntryChipStateInterface} from 'notebook/notes/core-ui/useEntryChipState';
import notifications from 'notifications';
import * as Perf from 'performance/Perf';
import {triggerSearchFromInternalLink} from 'search/saved-searches/SavedSearchUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as keyboard from 'util/Keyboard';
import * as logging from 'util/Logging';
import memoizeLastCall from 'util/memoizeLastCall';
import * as NameUtil from 'util/NameUtil';
import sanitizeLink from 'util/sanitizeLink';
import {ElementClipboardEvent, ElementKeyboardEvent, ElementMouseEvent} from 'util/Types';

import APIUtil from '../../../util/APIUtil';
import {SelectionPartition} from '../EditorTypes';
import {EMPTY_SEARCH_RESULT_PROPS, MediocreSearchResultProps} from '../search/SearchResultProps';

type FormatHandler = (args: {format: string; partition: SelectionPartition; data?: FormatData}) => void;

// 1 minute
const TYPING_PERF_INTERVAL = 60 * 1000;

interface TypingPerfConfig {
  metricName: string;
  // Additional options such as aggregations may be added here if more flexibiilty is needed
}

interface ChildComponentProps<TIndex> {
  key: number;
  className: string;
  style: React.CSSProperties;
  contentEditable: boolean;
  dangerouslySetInnerHTML: {__html: string};
  'data-cid': string;
  'data-index': TIndex;
  'data-allow-multiline': boolean | undefined;
  ref?: React.RefObject<HTMLDivElement>;
  onClick: (e: ElementMouseEvent) => void;
  onMouseDown: ((e: React.MouseEvent<unknown, MouseEvent>) => void) | undefined;
  onMouseOver: ((e: React.MouseEvent<unknown, MouseEvent>) => void) | undefined;
  onCopy: (e: ElementClipboardEvent) => void;
  onCompositionStart?: () => void;
  onCompositionEnd?: () => void;
  onCompositionUpdate?: () => void;
  spellCheck?: boolean;
  onInput?: () => void;
  onPaste?: (e: ElementClipboardEvent) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  onCut?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onDoubleClick?: () => void;
  onWheel?: () => void;
  onSelect?: () => void;
  tabIndex?: number;
  'data-formattable'?: boolean;
  'data-allow-at-search'?: boolean;
  'data-allow-slash-search'?: boolean;
  'data-test-component'?: string;
  'data-test-element'?: string;
}

interface InternalEditableProps<TIndex> extends MediocreSearchResultProps {
  className?: string;
  style?: React.CSSProperties;
  index: TIndex;
  cid: string;
  text: string;
  allowHTML?: boolean;
  allowRichText?: boolean;
  allowMultiline?: boolean;
  allowAtSearch?: boolean;
  allowSlashSearch?: boolean;
  locked?: boolean;
  isProtected?: boolean;
  dataFormattable?: boolean;
  formats: Format | null;
  spellCheck?: boolean;
  chipDisplayNameOverrides?: UseEntryChipStateInterface | null;

  // Allow the parent component to decide if preventDefault is called when pasting to avoid a Firefox
  // bug. See: wyciwyp.extractImageFromPaste
  // TODO: Remove in favor of having every pasteHandler explicitly call preventDefault instead of
  // having this flag.
  allowDefaultPaste?: boolean;

  // Custom events
  updateHandler: (args: {
    text: string;
    target: Element | EventTarget;
    formats: Format;
    selection?: BenchlingSelection;
  }) => void;
  tabHandler?: (args: {partition: SelectionPartition; shiftKey: boolean; target: EventTarget}) => void;
  formatHandler?: FormatHandler;
  escapeHandler?: () => void;
  moveHandler?: (args: {up: boolean; beginning: boolean}) => void;
  pasteHandler?: (
    args: SelectionPartition & {
      originalEvent: ElementClipboardEvent;
    }
  ) => void;
  mergeUpHandler?: (args: {anchor: boolean}) => void;
  splitHandler?: (args: {
    before: string;
    after: string;
    afterPoint: boolean;
    selected: string;
    modifierKey: boolean;
    shiftKey: boolean;
    originalEvent: ElementKeyboardEvent;
  }) => void;
  multiSelectHandler?: (args: {up: boolean}) => void;
  storeState?: (args: {selection: BenchlingSelection}) => void;
  restoreState?: (
    e: ElementKeyboardEvent,
    selectionRestoreData: {selection: SelectionRestoreData}
  ) => boolean;
  // Proxied standard events
  arrowKeydown?: (e: ElementKeyboardEvent) => void;
  mouseDownHandler?: (e: React.MouseEvent<unknown, MouseEvent>) => void;
  clickHandler?: (e: ElementMouseEvent) => void;
  mouseOverHandler?: () => void;
  focusHandler?: () => void;
  blurHandler?: () => void;
  dblClickHandler?: () => void;
  wheelHandler?: () => void;
  selectHandler?: () => void;

  // Extra hooks
  keyDownHook?: (e: ElementKeyboardEvent) => boolean | void;

  typingPerfConfig?: TypingPerfConfig;

  /** A ref to the underlying editable DOM element */
  rootRef?: React.RefObject<HTMLDivElement>;
}

const DEFAULT_PROPS = {
  spellCheck: false,
  locked: false,
  isProtected: false,
  allowAtSearch: false,
  allowSlashSearch: false,
  allowDefaultPaste: false,
  ...EMPTY_SEARCH_RESULT_PROPS,
};

export type EditableProps<TIndex> = Omit<InternalEditableProps<TIndex>, keyof typeof DEFAULT_PROPS> &
  Partial<typeof DEFAULT_PROPS>;

// Keep in sync with link_search.py
const INTERNAL_LINKABLE_MODELS_WITHOUT_REQUEST_ACCESS = [
  ModelTypeConstants.BATCH,
  ModelTypeConstants.CONNECTION,
  ModelTypeConstants.ENTITY,
  ModelTypeConstants.PROCESS_OUTPUT,
  ModelTypeConstants.PROCESS_TASK,
  ModelTypeConstants.SAVED_SEARCH,
  ModelTypeConstants.TEAM,
  ModelTypeConstants.WORKLIST,
  // ModelTypeConstants.DEPRECATED_INSTRUMENT, -- Not a valid type on the frontend?
];

class EditableComponent<TIndex> extends React.Component<EditableProps<TIndex>> {
  sanityCheckCompositionOn = false;
  isComposing = false;
  childKey = 0;
  urlMap: {[url: string]: number} = {};
  $tooltipEls: JQuery | null = null;
  typingPerfId: Perf.PerfId = Perf.PERF_ID_DISABLED;
  rootRef = React.createRef<HTMLDivElement>();

  _getRootRef(): React.RefObject<HTMLDivElement> {
    return this.props.rootRef ?? this.rootRef;
  }

  override shouldComponentUpdate(nextProps: EditableProps<TIndex>): boolean {
    if (this.isComposing) {
      return false;
    }
    if (!this.props.allowHTML) {
      return !isEqual(nextProps, this.props);
    }

    // For the allowHTML case, we skip update if only the text has changed
    // and the EditableComponent is already rendering the new text (which happens
    // often because the EditableComponent is uncontrolled for the input event).
    // eslint-disable-next-line no-restricted-syntax
    if (!isEqual(_.omit(nextProps, 'text'), _.omit(this.props, 'text'))) {
      return true;
    }

    // If we don't have a handle on the root, update; if we do, only update if the HTML content has changed.
    return this.processHTML(nextProps.text) !== this._getRootRef().current?.innerHTML;
  }

  static defaultProps = DEFAULT_PROPS;

  computeState(): {selection: BenchlingSelection} {
    return {selection: selutil.getSelectionIndices()};
  }

  storeState(): void {
    if (this.props.storeState) {
      this.props.storeState(this.computeState());
    }
  }

  /**
   * Delete chip if chip is found to the left of cursor - for backspace keydown event
   *
   * Chips are implemented as non-contentEditables embedded within the EditableComponent contentEditable.
   * Some browsers like Chrome are able to handle the intuitive behavior that the entire chip should be
   * deleted and then trigger the appropriate onInput handler. Unfortunately, this doesn't have
   * cross-browser support (notably Firefox). So instead of trusting the browser's DOM modifications, we
   * use JavaScript to update our formatted text model as well as the desired selection.
   */
  _handleChipBackspace(e: ElementKeyboardEvent): void {
    assert(e.which === keyboard.BACKSPACE, 'Expected backspace for backspace handler');
    if (!this.props.allowRichText) {
      return;
    }

    const partition = selutil.partitionSelection({target: e.currentTarget});
    if (partition == null) {
      return;
    }
    const {selected, before, afterPoint} = partition;
    if (selected !== '' || !afterPoint) {
      return;
    }
    const offset = before.length;
    // Since selected='' and afterPoint=true, the cursor is expected to be directly to the right of a chip.
    // Spliting with afterPoint=false means that middleAfterFormats will start with the chip.
    const {before: beforeFormats, after: middleAfterFormats} = formatters.splitFormats(
      this.props.formats,
      offset,
      /* afterPoint */ false
    );
    // Splitting middleAfterFormats with afterPoint=true at index=0 means we are basically lobbing off
    // the chip and afterFormats will contain the remaining formats.
    const {after: afterFormats} = formatters.splitFormats(middleAfterFormats, 0, /* afterPoint */ true);
    const newFormats = formatters.joinFormats(beforeFormats, afterFormats, offset);

    // Chips are given one unit (FormattersConstants.CHIP_WIDTH) in our selection indices, so we offset
    // the selection to move the cursor to the correct location.
    const selection = selutil.getSelectionIndices();
    const newSelection = {
      cid: selection.cid,
      start: selection.start - FormattersConstants.CHIP_WIDTH,
      end: selection.end - FormattersConstants.CHIP_WIDTH,
    };

    e.preventDefault();
    this.props.updateHandler({
      text: this.props.text,
      target: e.target,
      formats: newFormats,
      selection: newSelection,
    });
  }

  keyDownHandler = (e: ElementKeyboardEvent): void => {
    // If the parent is handling Ctrl-Z, let them handle the event.
    let partition;
    if (keyboard.isUndoRedo(e) && this.props.restoreState) {
      e.preventDefault();
      this.props.restoreState(e, this.computeState());
      return;
    }

    // If the parent would like to handle arrow events entirely, let them.
    if (this.props.arrowKeydown != null && [37, 38, 39, 40].includes(e.which)) {
      this.props.arrowKeydown(e);
      return;
    }

    if (this.props.keyDownHook && this.props.keyDownHook(e)) {
      return;
    }

    // start typing perf after short circuiting on custom handler overrides
    Perf.markStart(this.typingPerfId);

    try {
      switch (e.which) {
        case 13: // ENTER
          e.preventDefault();
          if (this.props.splitHandler != null) {
            partition = selutil.partitionSelection({target: e.currentTarget});
            if (partition == null) {
              return;
            }
            const {before, selected, afterPoint, after} = partition;
            this.storeState();
            this.props.splitHandler({
              before,
              after,
              afterPoint,
              selected,
              modifierKey: e.metaKey || e.ctrlKey || e.altKey,
              shiftKey: e.shiftKey,
              originalEvent: e,
            });
          }
          break;
        case keyboard.BACKSPACE:
          this.storeState();
          if (selutil.selectionIsAtBeginning(e)) {
            e.preventDefault();
            if (this.props.mergeUpHandler != null) {
              this.props.mergeUpHandler({anchor: false});
            }
          } else {
            this._handleChipBackspace(e);
          }
          break;
        case 37: // left
          if (!e.shiftKey && selutil.selectionIsAtBeginning(e)) {
            e.preventDefault();
            if (this.props.moveHandler) {
              this.props.moveHandler({up: true, beginning: false});
            }
          }
          break;
        case 38: // up
          if (!e.shiftKey) {
            if (selutil.selectionIsAtBeginning(e)) {
              e.preventDefault();
              if (this.props.moveHandler) {
                this.props.moveHandler({up: true, beginning: true});
              }
            } else if (selutil.selectionIsAtEnd(e) && !this.props.allowMultiline) {
              e.preventDefault();
              if (this.props.moveHandler) {
                this.props.moveHandler({up: true, beginning: false});
              }
            }
          } else if (selutil.selectionBeginsAtBeginning(e)) {
            e.preventDefault();
            if (this.props.multiSelectHandler) {
              this.props.multiSelectHandler({up: true});
            }
            break;
          }
          break;
        case 39: // right
          if (!e.shiftKey && selutil.selectionIsAtEnd(e)) {
            e.preventDefault();
            if (this.props.moveHandler) {
              this.props.moveHandler({up: false, beginning: true});
            }
          }
          break;
        case 40: // down
          if (!e.shiftKey) {
            if (selutil.selectionIsAtBeginning(e) && !this.props.allowMultiline) {
              e.preventDefault();
              if (this.props.moveHandler) {
                this.props.moveHandler({up: false, beginning: true});
              }
            } else if (selutil.selectionIsAtEnd(e)) {
              e.preventDefault();
              if (this.props.moveHandler) {
                this.props.moveHandler({up: false, beginning: false});
              }
            }
          } else if (selutil.selectionEndsAtEnd(e)) {
            e.preventDefault();
            if (this.props.multiSelectHandler) {
              this.props.multiSelectHandler({up: false});
            }
          }
          break;
        case 27: // ESC
          e.preventDefault();
          if (this.props.escapeHandler) {
            this.props.escapeHandler();
          }
          break;
        case 9: // tab
          e.preventDefault();
          if (this.props.tabHandler != null) {
            this.storeState();
            partition = selutil.partitionSelection({target: e.currentTarget});
            if (partition == null) {
              return;
            }
            this.props.tabHandler({partition, shiftKey: e.shiftKey, target: e.target});
          }
          break;
        case 46: // DEL
          if (selutil.selectionIsAtEnd(e)) {
            e.preventDefault();
            if (this.props.mergeUpHandler != null) {
              this.storeState();
              this.props.mergeUpHandler({anchor: true});
            }
          }
          break;
        default:
          if (e.metaKey || e.ctrlKey) {
            // Handle formatting
            const format = formatters.formatTypeFromKeydown(e);
            if (format != null) {
              e.preventDefault();
              if (this.props.allowRichText) {
                partition = selutil.partitionSelection(e);
                if (partition == null) {
                  return;
                }
                if (this.props.formatHandler) {
                  this.props.formatHandler({
                    format,
                    partition,
                    data: {
                      trigger: FormatDataTrigger.KEYBOARD_SHORTCUT,
                    },
                  });
                }
              }
            }
            // Prevent Ctrl-S from triggering browser's save page behavior
            if (keyboard.isCtrlS(e)) {
              e.preventDefault();
            }
          } else {
            // Normal entry
            this.storeState();
          }
      }
    } finally {
      Perf.markEnd(this.typingPerfId);
    }
  };

  // Note: Text input events should also call storeState() to maintain undo/redo functionality.

  private handleUrlPasteWithSelection = (
    partition: SelectionPartition,
    sanitizedUrl: string,
    formatHandler: FormatHandler
  ): void => {
    this.storeState();
    formatHandler({
      format: FormattersConstants.SET_LINK,
      partition: {...partition},
      data: {url: sanitizedUrl},
    });
  };

  private handleUrlPasteWithoutSelection = (
    partition: SelectionPartition,
    clipboardText: string,
    sanitizedUrl: string,
    formatHandler: FormatHandler
  ): void => {
    this.storeState();
    const {before, after, afterPoint} = partition;
    formatHandler({
      format: FormattersConstants.SET_LINK,
      partition: {
        before,
        after,
        selected: '',
        afterPoint,
      },
      data: {url: sanitizedUrl, text: clipboardText},
    });
  };

  private handleCustomPaste = (
    e: ElementClipboardEvent,
    partition: SelectionPartition,
    pasteHandler: (args: SelectionPartition & {originalEvent: ElementClipboardEvent}) => void
  ): void => {
    const {before, selected, after, afterPoint} = partition;
    this.storeState();
    pasteHandler({
      before,
      after,
      selected,
      afterPoint,
      originalEvent: e,
    });
  };

  private extractLinkFromClipboardHtml = (
    clipboardData: DataTransfer
  ): {url: string; displayText: string} | null => {
    if (!clipboardData || !clipboardData.types) {
      return null;
    }
    if (!wyciwyp.typesContains(clipboardData.types, 'text/html')) {
      return null;
    }

    try {
      const html = wyciwyp.getHTMLData(clipboardData);
      const $data = wyciwyp.jQuerySafeParseHTML(html);

      const $links = $data.find('a');
      if ($links.length !== 1) {
        return null;
      }

      // The text must be the entirety of the link
      const textContent = $data.text().trim();
      const linkText = $links.first().text().trim();
      if (textContent !== linkText) {
        return null;
      }

      const $link = $links.first();
      const url = $link.attr('href');
      const displayText = linkText;

      if (!url || !displayText) {
        return null;
      }

      const sanitizedUrl = sanitizeLink(url);
      if (!sanitizedUrl) {
        return null;
      }

      return {url: sanitizedUrl, displayText};
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      // HTML parsing errors are expected and should be handled gracefully
      logging.info(`Failed to parse clipboard HTML for link extraction: ${e.toString()}`);
      return null;
    }
  };

  pasteHandler = (e: ElementClipboardEvent): void => {
    const partition = selutil.partitionSelection({target: e.currentTarget});
    if (partition == null) {
      this.storeState();
      return;
    }

    const hasSelection = partition.selected.length > 0;
    const canFormat = this.props.formatHandler != null;
    const hasCustomPasteHandler = this.props.pasteHandler != null;

    const richLink = this.extractLinkFromClipboardHtml(e.clipboardData);
    if (richLink && canFormat) {
      e.preventDefault();
      if (hasSelection) {
        this.handleUrlPasteWithSelection(partition, richLink.url, this.props.formatHandler);
      } else {
        this.handleUrlPasteWithoutSelection(
          partition,
          richLink.displayText,
          richLink.url,
          this.props.formatHandler
        );
      }
      return;
    }

    // Fall back to plain text URL detection
    const clipboardText = e.clipboardData?.getData('text/plain').trim() ?? '';
    const sanitizedUrl = sanitizeLink(clipboardText);

    const shouldApplyUrlToSelection = sanitizedUrl && hasSelection && canFormat;
    if (shouldApplyUrlToSelection) {
      e.preventDefault();
      this.handleUrlPasteWithSelection(partition, sanitizedUrl, this.props.formatHandler);
      return;
    }

    const shouldInsertUrlAsLink = sanitizedUrl && !hasSelection && canFormat;
    if (shouldInsertUrlAsLink) {
      e.preventDefault();
      this.handleUrlPasteWithoutSelection(partition, clipboardText, sanitizedUrl, this.props.formatHandler);
      return;
    }

    if (hasCustomPasteHandler) {
      if (!this.props.allowDefaultPaste) {
        e.preventDefault();
      }
      this.handleCustomPaste(e, partition, this.props.pasteHandler);
      return;
    }

    this.storeState();
  };

  _handleInput = (): void => {
    // Because we are doing a scary thing of skipping rendering when isComposing, we sanity check that
    // the flag is even warranted (i.e. that we think we are actually still in composition mode, which may
    // not be the case if for reason the DOM node get re-created after compositionstart but before
    // compositionend fires). Every compositionupdate (which will occur alongside compositionstart and
    // compositionend) will precede the input event, so we set a sanity bit in _handleCompositionUpdate and
    // check it here in _handleInput.
    if (this.isComposing && !this.sanityCheckCompositionOn) {
      this.isComposing = false;
      logging.info(
        'Unexpectedly saw EditableComponent in composing mode, without composition events: ' +
          'ignoring composition',
        {extra: {outerHtml: this._getRootRef().current!.outerHTML}}
      );
    }
    this.sanityCheckCompositionOn = false;
    this._processEdit();
  };

  _handleCompositionStart = (): void => {
    // Block re-renders of this component (see shouldComponentUpdate), when the input is in compose mode.
    this.isComposing = true;
  };

  _handleCompositionUpdate = (): void => {
    this.sanityCheckCompositionOn = true;
  };

  _handleCompositionEnd = (): void => {
    // This is largely inspired by https://github.com/facebook/draft-js/blob/67c5e69499e3b0c149ce83b004872afdf4180463/src/component/handlers/composition/DraftEditorCompositionHandler.js
    //
    // The case involving multiple composeEnd events firing rapidly is not handled here, because it would
    // introduce more complexity: to use setTimeout to throttle the events would result in
    // this.props.updateHandler to be called outside of the react event life cycle, causing any setStates to
    // be executed *synchronously* with multiple re-renders, where they would otherwise be batched together
    // in one re-render. This would cause drastic regression in interacting with this component:
    //
    // EntryActions.updateNote(...)
    //   [before] Store is updated, component marked for re-render.
    //   [after]  Store is updated, component re-rerenders, EditableComponent re-renders with new DOM nodes,
    //            thereby wiping the selection.
    // SelectionActions.setSelection({selection: selutil.getSelectionIndicies()});
    //   [before] SelectionStore is updated to restore to where the selection was before (no DOM changes have
    //            happened yet)
    //   [after]  SelectionStore is updated to restore to {start: 0, end: 0} as we re-rendered and didn't
    //            restore selection.
    // Re-render:
    //   [before] Desired selection is restored after the component re-renders from SelectionStore
    //   [after]  Selection is stored to {start: 0, end: 0} which effectively results in unexpected RTL.
    //
    // To handle this better in the future (esp. if we end up needing go use setTimeout here), we should do:
    //   selection = selutil.getSelectionIndicies();
    //   EntryActions.updateNote(...);
    //   SelectionActions.setSelection({selection});
    //
    // for all components that render <EditableComponent /> and have an update handler that calls
    // setSelection/requestSelection.
    if (!this.isComposing) {
      return;
    }
    this.isComposing = false;
    // Now that composition has ended, we can register the edit with the parent component.
    this._processEdit();
  };

  _processEdit(): void {
    const target = this._getRootRef().current!;
    // Do some processing on the text first, then pass it up.
    const formattedText = formatters.getFormattedTextFromRenderedElement(target);
    let {text} = formattedText;
    const {formats} = formattedText;
    // Here we remove a trailing newline if it exists. The render method appends a trailing newline to get
    // around `whitespace: pre-wrap` ignoring the last newline. We do not want to have this trailing newline
    // propagate to our model.
    if (text.slice(-1) === '\n' && this.props.allowMultiline) {
      text = text.slice(0, -1);
    }
    this.props.updateHandler({text, target, formats});
    if (!this.isComposing) {
      if (BenchlingConfig.ELN_TABLE_IME_SUPPORT) {
        // on compositionend, the below rerender causes the cursor to jump back to the start of the input,
        // making IMEs type backwards. This saves the cursor state just before the rerender and restores
        // it afterward.
        const savedSelection = selutil.getSelectionIndices();
        this.forceUpdate(() => {
          if (savedSelection) {
            selutil.restoreSelection(this._getRootRef().current!, savedSelection);
          }
        });
      } else {
        this.forceUpdate();
      }
    }
  }

  clickHandler = (e: ElementMouseEvent): void => {
    if (this.props.clickHandler != null) {
      this.props.clickHandler(e);
      return;
    }

    const $link = $(e.target).closest('a');
    if ($link.length === 0) {
      return;
    }
    const url = $link.attr('href');
    const $inaccessibleLink = $(e.target).closest('.internalLink[data-invalid-id]');

    if ($inaccessibleLink.length) {
      e.preventDefault();
      const invalidId = $inaccessibleLink.attr('data-invalid-id');
      const modelType = APIUtil.apiIdToType(invalidId);
      if (INTERNAL_LINKABLE_MODELS_WITHOUT_REQUEST_ACCESS.includes(modelType)) {
        notifications.warn(
          `The ${NameUtil.typeToName(
            modelType
          )} you are trying to access has either been deleted or you don't have permission to view it`
        );
      } else {
        requestAccess({canRequestAccess: true, invalidId});
      }
      return;
    } else if ($link.closest('.internalLink').length) {
      // Open internal links with a single click
      if (!BenchlingUtil.isSimpleClick(e)) {
        return;
      }

      const $newLink = $link.closest('.internalLink');
      const internalLink = parse($newLink.attr('data-internal-link'));

      e.preventDefault();
      if (internalLink.savedSearchData) {
        triggerSearchFromInternalLink(internalLink.savedSearchData);
        return;
      } else if (internalLink.id != null && APIUtil.isScratchPlate(internalLink.id)) {
        openScratchPlateChipModal({scratchPlateId: internalLink.id, plateName: internalLink.name});
        return;
      } else {
        openLink(url);
      }
    }

    // Open all links with CMD-click on mac, CTRL-click on windows (CTRL-click on mac is right click)
    if (keyboard.isOSCtrlKey(e) || this._isEditingDisabled()) {
      e.preventDefault();
      openLink(url);
    }
  };

  cutHandler = (): void => {
    this.storeState();
  };

  forceContentEditableUpdate(forceUpdate?: boolean): void {
    // Increments the key on the contenteditable, which will cause it to
    // rerender on next render.

    // Call this method in the case where the user has inputted text, but we don't actually
    // want to modify the state. The contentEditable will be wrong, but React won't know!

    // The specific cases where this affects us is if we replace the contents of the contentEditable with
    // the text that was there before the inputHandler, e.g.
    //    actual dom   |  react virtual dom
    // ----------------|--------------------
    //     "hello"     |     "hello"
    //          user types "!"
    //     "hello!"    |    "hello"
    //
    // At this point, if we decide user can't use punctuation and try to set the text to "hello", nothing
    // will happen, as React thinks the virtual DOM matches the new text.
    //
    // TimeEditable:
    //  > Normal Case:
    //    actual dom   |  react virtual dom
    // ----------------|--------------------
    //     "11:11:11"  |     "11:11:11"
    //  > user types "1"
    //     "11:11:112" |    "11:11:11"
    //  > We truncate from the left, and set the text to "11:11:12", which is different from the virtual dom.
    //     "11:11:11"  |    "11:11:12"
    //  >  "11:11:12"  |    "11:11:12"
    //
    //  > Bad Case:
    //    actual dom   |  react virtual dom
    // ----------------|--------------------
    //     "11:11:11"  |     "11:11:11"
    //  > user type "1"
    //     "11:11:111" |     "11:11:11"
    //  > truncate from left down to 6 chars, so we set text to: "11:11:11", but this matches the virtual
    //  > DOM, so nothing happens.
    //     "11:11:111" |     "11:11:11"
    //
    // Multi-line inputs
    //    actual dom   |  react virtual dom
    // ----------------|--------------------
    //     "hello\n"   |     "hello\n"
    //  > user deletes newline
    //     "hello"     |     "hello\n"
    //  > we require a newline at the end to avoid a pre-wrap issue, so we set text to hello\n, which matches
    //  > the virual DOM, so nothing happens.
    //     "hello"     |     "hello\n"
    //  > so nothing happens.

    if (forceUpdate == null) {
      forceUpdate = true;
    }
    this.childKey++;
    if (forceUpdate) {
      this.forceUpdate(); // force a re-render in case the component isn't scheduled to update.
    }
  }

  _intializeTooltips(): void {
    // EditableComponent does not use React to manage it's children, and in fact, re-renders all of its
    // children as serialized HTML; therefore, we imperatively add tooltips, and destroy / re-create them
    // on each render.
    this.$tooltipEls = $(this._getRootRef().current!).find('[data-toggle="tooltip"]').tooltip({
      container: 'body',
    });
  }

  _destroyTooltips(): void {
    // Avoid leaking memory if a DOM node is removed on next render.
    this.$tooltipEls?.tooltip('destroy');
    this.$tooltipEls = null;
  }

  _exposeFormatsToFormatState(): void {
    $(this._getRootRef().current!).data('formats', this.props.formats);
  }

  constructor(props: EditableProps<TIndex>) {
    super(props);
    this.sanityCheckCompositionOn = false;
    this.isComposing = false;
    this.childKey = 0;
    if (props.typingPerfConfig) {
      this.typingPerfId = Perf.start(
        props.typingPerfConfig.metricName,
        {enabled: false},
        {
          interval: TYPING_PERF_INTERVAL,
          aggregations: [Perf.Aggregation.MEDIAN, Perf.Aggregation.MAX, Perf.Aggregation.COUNT],
        }
      );
    }
  }

  override componentDidMount(): void {
    this.urlMap = linkComponentDidMount(this._getRootRef().current!);
    this._intializeTooltips();
    this._exposeFormatsToFormatState();
  }

  override componentWillUnmount(): void {
    linkComponentWillUnmount(this._getRootRef().current!);
    this._destroyTooltips();
    Perf.stop(this.typingPerfId);
  }

  override UNSAFE_componentWillUpdate(nextProps: EditableProps<TIndex>): void {
    this._destroyTooltips();
  }

  override componentDidUpdate(): void {
    this.urlMap = linkComponentDidUpdate(this._getRootRef().current!, this.urlMap);
    this._intializeTooltips();
    this._exposeFormatsToFormatState();
  }

  override UNSAFE_componentWillReceiveProps(nextProps: EditableProps<TIndex>): void {
    if (nextProps.spellCheck !== this.props.spellCheck) {
      // Updating the spellCheck attribute does not suffice to toggle existing red squiggly lines,
      // so we force render (which will render an entirely new contentEditable)
      // See: https://code.google.com/p/chromium/issues/detail?id=174578
      //      https://code.google.com/p/chromium/issues/detail?id=155781
      this.forceContentEditableUpdate();
    }
  }

  _getRichHTMLMemo = memoizeLastCall(
    (
      text = '',
      formats = null,
      searchResultsTree = null,
      selectedSearchResultTree = null,
      chipDisplayNameOverrides: UseEntryChipStateInterface | null | undefined = {}
    ) =>
      formatters.renderHTML(text, formats ?? formatters.defaultFormats(), {
        searchResultsTree,
        selectedSearchResultTree,
        chipDisplayNameOverrides,
      })
  );

  getRichHTML(): string {
    return this._getRichHTMLMemo(
      this.props.text,
      this.props.formats,
      this.props.searchResultsTree,
      this.props.selectedSearchResultTree,
      this.props.chipDisplayNameOverrides
    );
  }

  processHTML(html: string): string {
    // pre-wrap ignores a trailing newline.
    // So we force there to always be a
    // newline, which allow the user to hit
    // enter to end/start a paragraph.
    if (this.props.allowMultiline) {
      html += '\n';
    }
    return html;
  }

  _handleCopy = (e: ElementClipboardEvent): void => {
    const selection = selutil.partitionSelection({target: e.currentTarget});
    if (!selection) {
      return;
    }
    const {before, selected, afterPoint} = selection;
    const {allowRichText, formats} = this.props;
    if (allowRichText) {
      const {after: selectedAfterFmts} = formatters.splitFormats(formats, before.length, afterPoint);
      const {before: selectedFmts} = formatters.splitFormats(selectedAfterFmts, selected.length);
      const html = formatters.renderHTML(selected, selectedFmts, {} /* no search results */);
      e.clipboardData.setData('text/html', html);
    }
    e.clipboardData.setData('text/plain', selected);
    e.preventDefault();
  };

  /* Determines if this editable should actually be editable. */
  _isEditingDisabled = (): boolean => Boolean(this.props.locked) || Boolean(this.props.isProtected);

  _hasSearchResults = (): boolean => Boolean(this.props.searchResultsTree);

  override render(): JSX.Element {
    const style = this.props.style != null ? this.props.style : {};
    const isEditingDisabled = this._isEditingDisabled();

    let html;
    if (this.props.allowHTML) {
      html = this.props.text;
    } else if (this.props.allowRichText || this._hasSearchResults()) {
      html = this.getRichHTML();
    } else {
      html = _.escape(this.props.text);
    }
    html = this.processHTML(html);

    const props: ChildComponentProps<TIndex> = {
      key: this.childKey,
      className: classNames(this.props.className, 'editable', {
        selectable: isEditingDisabled,
        'is-locked': isEditingDisabled,
      }),
      style,
      contentEditable: !isEditingDisabled,
      dangerouslySetInnerHTML: {__html: html},
      'data-cid': this.props.cid,
      'data-index': this.props.index,
      'data-allow-multiline': this.props.allowMultiline,
      onClick: this.clickHandler,
      onMouseDown: this.props.mouseDownHandler,
      onMouseOver: this.props.mouseOverHandler,
      onCopy: this._handleCopy,
      ...getTestProps({component: 'EditableComponent', element: 'root'}),
    };
    // TODO: Most of these events should be handled in some form (warn instead of no-op)
    if (!isEditingDisabled) {
      // eslint-disable-next-line no-restricted-syntax
      _.extend(props, {
        onCompositionStart: this._handleCompositionStart,
        onCompositionEnd: this._handleCompositionEnd,
        onCompositionUpdate: this._handleCompositionUpdate,
        spellCheck: this.props.spellCheck,
        onInput: this._handleInput,
        onPaste: this.pasteHandler,
        onKeyDown: this.keyDownHandler,
        onCut: this.cutHandler,
        onFocus: this.props.focusHandler,
        onBlur: this.props.blurHandler,
        onDoubleClick: this.props.dblClickHandler,
        onWheel: this.props.wheelHandler,
        onSelect: this.props.selectHandler,
        tabIndex: -1,
      });
    }

    if (!isEditingDisabled && (this.props.dataFormattable || this.props.allowRichText)) {
      props['data-formattable'] = true;
    }

    if (this.props.allowAtSearch) {
      props['data-allow-at-search'] = true;
    }
    if (this.props.allowSlashSearch) {
      props['data-allow-slash-search'] = true;
    }

    return <div {...props} ref={this._getRootRef()} />;
  }
}

// Wrap the EditableComponent with a functional component to make use of the useEntryChipState hook
function EditableWithRef<TIndex>(
  props: EditableProps<TIndex>,
  ref: React.Ref<EditableComponent<TIndex>>
): JSX.Element {
  const chipsState = useEntryChipState();

  return <EditableComponent {...props} ref={ref} chipDisplayNameOverrides={chipsState} />;
}

const Editable = React.forwardRef(EditableWithRef);

export default Editable;
